import React from "react";
import { overlayBase, useParticles } from "./_shared";
import LightSweep from "./LightSweep";

interface Props {
  /** Number of sparkle glints, default 10 */
  count?: number;
  /** Sparkle colour, default white */
  color?: string;
}

/**
 * SparkleShine — small cross-shaped glint particles combined with a light
 * sweep. Great for jewellery, glass, or polished surface banners.
 */
const SparkleShine: React.FC<Props> = ({
  count = 10,
  color = "#ffffff",
}) => {
  const parts = useParticles(
    count,
    (i, r) => ({
      id: i,
      left: r(0) * 100,
      top: r(1) * 100,
      size: 4 + r(2) * 5,
      delay: r(3) * 3,
      d: 1.2 + r(4) * 1.4,
    }),
    [count]
  );

  return (
    <div style={overlayBase}>
      <style>{`
        @keyframes oot-glint {
          0%, 100% { opacity: 0; transform: scale(0) rotate(0deg); }
          50%       { opacity: 1; transform: scale(1) rotate(90deg); }
        }
      `}</style>
      {parts.map((p) => (
        <div
          key={p.id}
          style={{
            position: "absolute",
            left: `${p.left}%`,
            top: `${p.top}%`,
            width: p.size,
            height: p.size,
            animation: `oot-glint ${p.d}s ease-in-out ${p.delay}s infinite`,
          }}
        >
          {/* Vertical arm of the cross */}
          <div
            style={{
              position: "absolute",
              inset: 0,
              background: `linear-gradient(0deg, transparent, ${color}, transparent)`,
              width: 1.5,
              left: "50%",
              transform: "translateX(-50%)",
              height: "100%",
            }}
          />
          {/* Horizontal arm of the cross */}
          <div
            style={{
              position: "absolute",
              inset: 0,
              background: `linear-gradient(90deg, transparent, ${color}, transparent)`,
              height: 1.5,
              top: "50%",
              transform: "translateY(-50%)",
              width: "100%",
            }}
          />
        </div>
      ))}
      <LightSweep color="rgba(255,255,255,0.4)" duration={4} />
    </div>
  );
};

export default SparkleShine;
